"use client";

import Head from 'next/head'
import AuthCard from '@/components/AuthCard'
import AuthValidationErrors from '@/components/AuthValidationErrors'
import GuestLayout from '@/components/Layouts/GuestLayout'
import Link from 'next/link'
import { useAuth } from '@/hooks/auth'
import { useState } from 'react'
import AuthContainer from '@/components/AuthContainer'
import { useLoadScript } from "@react-google-maps/api"
import { GOOGLE_MAPS_LIBRARIES } from '@/lib/googleMapsLibraries'
import Modal from '@/components/Modal'
import TermsAndConditionsDriver from '@/components/TermsAndConditionsDriver'
import DriverRegistrationForm from '@/components/driver/DriverRegistrationForm'
import { useI18n } from '@/lib/I18nProvider'
import { useParams } from 'next/navigation'

const Driver = () => {
    const { t } = useI18n()
    const params = useParams()
    const slug = params?.slug
    const base = slug ? `/${slug}` : ''

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const { register } = useAuth({
        middleware: 'guest',
        redirectIfAuthenticated: '/dashboard',
    })

    const [formData, setFormData] = useState({
        name: '',
        lastname: '',
        email: '',
        password: '',
        password_confirmation: '',
        address: '',
        postal_code: '',
        phone: '',
        emergency_name: '',
        emergency_tel: '',
        licence_num: '',
        car_model: ''
    })
    const [errors, setErrors] = useState([])
    const [success, setSuccess] = useState('')
    const [currentStep, setCurrentStep] = useState(1)
    const [showModal, setShowModal] = useState(false)
    const [agreedToTerms, setAgreedToTerms] = useState(false)
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)

    const onPlaceSelected = (destination, latLng, zipCode) => {
        setFormData(prev => ({
            ...prev,
            address: destination,
            postal_code: zipCode
        }))
    }

    const submitSuccess = () => {
        //setCurrentStep(currentStep + 1)
    }

    const submitForm = () => {
        register({
            ...formData,
            role: 'driver',
            setErrors,
            submitSuccess,
            setIsLoadingSubmit,
        })
    }

    const previous = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1)
        }
    }

    const next = () => {
        if (currentStep == 1) {
            if (!formData.name) {
                setErrors([t('merchants.validation.nameRequired')])
                return
            }
            if (!formData.lastname) {
                setErrors([t('clientPortal.register.driverLastNameRequired')])
                return
            }
            if (!formData.email) {
                setErrors([t('merchants.validation.emailRequired')])
                return
            }
            if (!formData.password) {
                setErrors([t('merchants.validation.passwordRequired')])
                return
            }
            if (formData.password.length < 8) {
                setErrors([t('merchants.validation.passwordMinLength')])
                return
            }
            if (!formData.password_confirmation) {
                setErrors([t('merchants.validation.passwordConfirmationRequired')])
                return
            }
            if (formData.password !== formData.password_confirmation) {
                setErrors([t('merchants.validation.passwordsDoNotMatch')])
                return
            }
            setErrors([])
            setCurrentStep(currentStep + 1)
        } else if (currentStep == 2) {
            if (!formData.address) {
                setErrors([
                    t('clientPortal.request.chooseAddressFromList'),
                ])
                return
            }
            if (!formData.phone) {
                setErrors([t('merchants.validation.phoneRequired')])
                return
            }
            if (!formData.emergency_name) {
                setErrors([t('clientPortal.register.emergencyContactRequired')])
                return
            }
            if (!formData.emergency_tel) {
                setErrors([
                    t('clientPortal.register.emergencyPhoneRequired'),
                ])
                return
            }
            if (!formData.car_model) {
                setErrors([
                    t('clientPortal.register.vehicleModelRequired'),
                ])
                return
            }
            setErrors([])
            submitForm()
        }
    }

    const handleTermsChange = () => {
        setAgreedToTerms(!agreedToTerms)
    }

    const handleShowTerms = () => {
        setShowModal(true)
    }

    return (
        <div>
            <Head>
                <title>{t('clientPortal.register.driverPageTitle')}</title>
            </Head>
            <div className="steps" id="stepsDemo">
                <div
                    className={`step-item ${
                        currentStep == 1 ? 'is-active is-success' : ''
                    } ${currentStep >= 1 ? 'is-completed' : ''}`}>
                    <div className="step-marker">1</div>
                    <div className="step-details">
                        <p className="step-title">{t('merchants.registration.account')}</p>
                    </div>
                </div>
                <div
                    className={`step-item ${
                        currentStep == 2 ? 'is-active is-success' : ''
                    } ${currentStep >= 2 ? 'is-completed' : ''}`}>
                    <div className="step-marker">2</div>
                    <div className="step-details">
                        <p className="step-title">{t('merchants.registration.profile')}</p>
                    </div>
                </div>
                <div
                    className={`step-item ${
                        currentStep == 3 ? 'is-active is-success' : ''
                    } ${currentStep >= 3 ? 'is-completed' : ''}`}>
                    <div className="step-marker">3</div>
                    <div className="step-details">
                        <p className="step-title">{t('merchants.registration.confirmation')}</p>
                    </div>
                </div>
                <div className="steps-content has-text-centered">
                    <AuthCard>
                        {/* Validation Errors */}
                        <h1 className="title">{t('clientPortal.register.driverHeading')}</h1>
                        {success ? (
                            <p className="has-text-success">
                                {success}
                                <br />
                                <br />
                            </p>
                        ) : null}
                        <AuthValidationErrors
                            className="mb-4"
                            errors={errors}
                        />

                        <div
                            className={`step-content ${
                                currentStep == 1 ? 'is-active' : ''
                            }`}>
                            <div>
                                <DriverRegistrationForm 
                                    formData={formData}
                                    setFormData={setFormData}
                                    errors={errors}
                                    currentStep={currentStep}
                                    isLoaded={isLoaded}
                                />
                                <div>
                                    <br />
                                    <Link href={`${base}/login`}>
                                        {t('clientPortal.register.alreadyHaveAccount')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div
                            className={`step-content ${
                                currentStep == 2 ? 'is-active' : ''
                            }`}>
                            <div>
                                <DriverRegistrationForm 
                                    formData={formData}
                                    setFormData={setFormData}
                                    errors={errors}
                                    currentStep={currentStep}
                                    isLoaded={isLoaded}
                                    onPlaceSelected={onPlaceSelected}
                                    agreedToTerms={agreedToTerms}
                                    onTermsChange={handleTermsChange}
                                    onShowTerms={handleShowTerms}
                                />
                            </div>
                        </div>
                        <div
                            className={`step-content ${
                                currentStep == 3 ? 'is-active' : ''
                            }`}>
                            <DriverRegistrationForm 
                                formData={formData}
                                setFormData={setFormData}
                                errors={errors}
                                currentStep={currentStep}
                                isLoaded={isLoaded}
                            />
                        </div>
                        <br />

                        <div
                            className={`steps-actions ${
                                currentStep == 3 ? 'is-hidden' : ''
                            }`}>
                            <div
                                className={`steps-action ${
                                    currentStep == 1 ? 'is-hidden' : ''
                                }`}>
                                <button
                                    className="button"
                                    onClick={() => {
                                        previous()
                                    }}>
                                    {t('common.back')}
                                </button>
                            </div>
                            <div className="steps-action">
                                <button
                                    disabled={currentStep == 2 && !agreedToTerms}
                                    className={`button is-primary ${isLoadingSubmit ? 'is-loading' : ''}`}
                                    onClick={() => {
                                        next()
                                    }}>
                                    {t('common.continue')}
                                </button>
                            </div>
                        </div>
                    </AuthCard>
                </div>
            </div>
            <Modal
                onClose={() => {
                    setShowModal(false)
                }}
                show={showModal}>
                <TermsAndConditionsDriver></TermsAndConditionsDriver>
            </Modal>
        </div>
    )
}

export default Driver